import MomentUtils from '@date-io/moment'
import { withWidth, WithStyles } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import { WithWidth } from '@material-ui/core/withWidth'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import i18n from 'i18next'
import moment from 'moment-timezone'
import { useState, useEffect, useLayoutEffect, FC } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { withRouter, RouteComponentProps, matchPath } from 'react-router-dom'
import { Dispatch } from 'redux'
import { navigate } from '../../actions/routes'
import { setWindowWidth } from '../../actions/users'
import { pushGTMEvent } from '../../config/googleTagManager'
import { objectNotEmpty } from '../../libs/objects'
import { selectApartmentById } from '../../selectors/apartments'
import {
  selectContentIdFromPathname,
  selectIsCreateApplicationRoute
} from '../../selectors/router'
import {
  selectIsAdminUser,
  selectUserIsAuthenticated,
  selectUserAttributes
} from '../../selectors/users'
import { UserAttributes } from '../../services/auth/types'
import {
  segmentIdentify,
  segmentLoadKey,
  segmentLogPage
} from '../../services/segment'
import { getCurrentLanguage } from '../../translations/i18n'
import { StateInterface } from '../../types/states'
import ApartmentBottomNav from './ApartmentBottomNav'
import NavBar from './Menus/NavBar'
import navigationStyles from './navigationStyles'
import 'moment/locale/fi'

moment.tz.setDefault('Europe/Helsinki')
const locales = {
  en: 'en',
  fi: 'fi'
}

interface tProps extends WithTranslation {
  t: i18n.TFunction
}

interface PropsFromState {
  isAuthenticated: boolean
  isAdminUser: boolean
  showLanguageSwitcher: boolean
  userAttributes: any
  hasStatistics: boolean
}

interface DispatchProps {
  doNavigation: (path: string) => void
  setWindowWidth: (windowWidth: string) => void
}

interface OwnProps extends RouteComponentProps {
  Routes: () => JSX.Element
}

type NavProps = OwnProps &
  DispatchProps &
  PropsFromState &
  WithStyles &
  WithWidth &
  tProps

/**
 * Takes Routes element from Router.tsx, everything above it is handling navigation
 */
const Navigation: FC<NavProps> = ({
  classes,
  isAuthenticated,
  isAdminUser,
  Routes,
  doNavigation,
  width,
  location: { pathname },
  showLanguageSwitcher,
  setWindowWidth,
  userAttributes,
  hasStatistics
}) => {
  useLayoutEffect(() => {
    pushGTMEvent('optimize.activate')
  }, [pathname])

  const [drawerOpen, toggleDrawer] = useState<boolean>(false)
  const handleToggleDrawer = () => toggleDrawer(!drawerOpen)
  const [locale, setLocale] = useState<string>(getCurrentLanguage())

  useEffect(() => {
    const currentLocale = getCurrentLanguage()
    setLocale(currentLocale)
  }, [locale, setLocale])

  //create toggle variable

  useEffect(() => {
    if (isAuthenticated) {
      segmentLoadKey()
      segmentIdentify()
    }
  }, [isAuthenticated])
  useEffect(() => {
    segmentLogPage()
  }, [pathname])
  useEffect(() => {
    setWindowWidth(width)
  }, [width])
  const isMobileViewport = useMediaQuery('(max-width:1065px)')
  const apartmentMatch = matchPath(pathname, {
    path: '/users/:userId/apartments/:apartmentId',
    exact: false,
    strict: false
  })
  const contractMatch = matchPath(pathname, {
    path: '/users/:userId/apartments/:apartmentId/contract/:applicationCode',
    exact: false,
    strict: false
  })
  const shouldShowMobileApartmentNav = Boolean(apartmentMatch) && isMobileViewport
  const isContractRoute = Boolean(contractMatch)
  const isStatisticsRoute = pathname.includes('/statistic')
  const apartmentBasePath = apartmentMatch?.url || ''
  return (
    <div className={classes.root}>
      <NavBar
        handleToggleDrawer={handleToggleDrawer}
        isAuthenticated={isAuthenticated}
        classes={classes}
        doNavigation={doNavigation}
        pathname={pathname}
        width={width}
        drawerOpen={drawerOpen}
        isAdminUser={isAdminUser}
        showLanguageSwitcher={showLanguageSwitcher}
        userAttributes={userAttributes}
        hasStatistics={hasStatistics}
      />

      <main
        className={classes.content}
        style={
          shouldShowMobileApartmentNav
            ? { paddingBottom: 'calc(82px + env(safe-area-inset-bottom))' }
            : undefined
        }
      >
        <div className={classes.toolbar} />
        <div className={classes.containerHolder}>
          <MuiPickersUtilsProvider
            libInstance={moment}
            utils={MomentUtils}
            locale={locales[locale]}
          >
            <Routes />
          </MuiPickersUtilsProvider>
        </div>
        {shouldShowMobileApartmentNav && (
          <div
            className={
              isStatisticsRoute
                ? `${classes.mobileBottomNavSpacer} ${classes.mobileBottomNavSpacerStatistics}`
                : classes.mobileBottomNavSpacer
            }
          />
        )}
      </main>
      {shouldShowMobileApartmentNav && (
        <ApartmentBottomNav
          basePath={apartmentBasePath}
          pathname={pathname}
          onNavigate={doNavigation}
          classes={classes}
          isContractRoute={isContractRoute}
          hasStatistics={hasStatistics}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: StateInterface) => {
  const stateUserAttributes = selectUserAttributes(state)
  const apartmentId = selectContentIdFromPathname(state)
  const apartment = apartmentId ? selectApartmentById(apartmentId)(state) : undefined
  return {
    isAuthenticated: selectUserIsAuthenticated(state),
    isAdminUser: selectIsAdminUser(state),
    showLanguageSwitcher: !selectIsCreateApplicationRoute(state),
    userAttributes: objectNotEmpty<UserAttributes>(stateUserAttributes)
      ? stateUserAttributes
      : undefined,
    hasStatistics: Boolean(apartment?.oviProStatistics)
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  doNavigation: (path: string) => dispatch(navigate(path)),
  setWindowWidth: (width: string) => dispatch(setWindowWidth(width))
})

export default withRouter(
  connect<PropsFromState, DispatchProps, OwnProps, StateInterface>(
    mapStateToProps,
    mapDispatchToProps
  )(
    withStyles(navigationStyles, { withTheme: true })(
      withWidth()(withTranslation()(Navigation))
    )
  )
)
